import React, { Suspense, useEffect, useState } from 'react'

const Lottie = React.lazy(() => import('lottie-react'))

/** Noto Emoji “hourglass with flowing sand” (U+23F3); vector loop via Lottie. */
const NOTO_HOURGLASS_FLOWING_LOTTIE_JSON_URL =
  'https://fonts.gstatic.com/s/e/notoemoji/latest/23f3/lottie.json'

/** Wikimedia `Hourglass.GIF` if Lottie fails to load (offline / CORS). */
const HOURGLASS_FALLBACK_GIF_URL =
  'https://upload.wikimedia.org/wikipedia/commons/5/5d/Hourglass.GIF'

export const DirectChallengeAwaitingHourglassLottie: React.FC<{
  className?: string
  style?: React.CSSProperties
}> = ({ className, style }) => {
  const [animationData, setAnimationData] = useState<object | null>(null)
  const [useFallback, setUseFallback] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    ;(async () => {
      try {
        const res = await fetch(NOTO_HOURGLASS_FLOWING_LOTTIE_JSON_URL, { signal: ac.signal })
        if (!res.ok) {
          setUseFallback(true)
          return
        }
        setAnimationData(await res.json())
      } catch {
        if (!ac.signal.aborted) setUseFallback(true)
      }
    })()
    return () => ac.abort()
  }, [])

  if (useFallback) {
    return (
      <img
        src={HOURGLASS_FALLBACK_GIF_URL}
        alt=""
        aria-hidden
        draggable={false}
        className={[className, 'object-contain'].filter(Boolean).join(' ')}
        style={style}
      />
    )
  }

  if (!animationData) {
    return (
      <div
        aria-hidden
        className={className}
        style={{ ...style, visibility: 'hidden' }}
      />
    )
  }

  return (
    <div className={className} style={style}>
      <Suspense fallback={<div className="h-full w-full" aria-hidden />}>
        <Lottie animationData={animationData} loop className="h-full w-full" />
      </Suspense>
    </div>
  )
}
